import { apiClient } from "./api-client";
import type { ContributionsStatsResponse } from "@/lib/types/stats";

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    if (!normalized) {
      return undefined;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const normalizeDurationSummary = (source: unknown, fallbackSource?: unknown) => {
  const record = source && typeof source === 'object' ? source as Record<string, unknown> : {};
  const fallbackRecord = fallbackSource && typeof fallbackSource === 'object'
    ? fallbackSource as Record<string, unknown>
    : {};

  // Helper to access nested recordingDuration
  const getNestedDurationMs = (obj: Record<string, unknown>) => {
    const nested = obj.recordingDuration && typeof obj.recordingDuration === 'object'
      ? (obj.recordingDuration as Record<string, unknown>)
      : null;
    return nested ? toFiniteNumber(nested.durationMs) : undefined;
  };

  const recordingDurationMs =
    toFiniteNumber(record.totalDurationMs) ??
    toFiniteNumber(record.recordingDurationMs) ??
    toFiniteNumber(record.durationMs) ??
    getNestedDurationMs(record) ??
    toFiniteNumber(fallbackRecord.totalDurationMs) ??
    toFiniteNumber(fallbackRecord.recordingDurationMs) ??
    toFiniteNumber(fallbackRecord.durationMs) ??
    getNestedDurationMs(fallbackRecord) ??
    0;

  const recordingDurationSeconds =
    toFiniteNumber(record.totalDurationSeconds) ??
    toFiniteNumber(record.recordingDurationSeconds) ??
    toFiniteNumber(fallbackRecord.totalDurationSeconds) ??
    toFiniteNumber(fallbackRecord.recordingDurationSeconds) ??
    Number((recordingDurationMs / 1000).toFixed(2));

  return {
    totalDurationMs: recordingDurationMs,
    totalDurationSeconds: recordingDurationSeconds,
    recordingDurationMs,
    recordingDurationSeconds,
    recordingDurationMinutes:
      toFiniteNumber(record.recordingDurationMinutes) ?? Number((recordingDurationMs / 60000).toFixed(2)),
    recordingDurationHours:
      toFiniteNumber(record.recordingDurationHours) ?? Number((recordingDurationMs / 3600000).toFixed(2)),
  };
};

const normalizeUserStats = (payload: ContributionsStatsResponse): ContributionsStatsResponse => {
  const root = payload && typeof payload === 'object' ? payload : {} as ContributionsStatsResponse;
  const global = root.global && typeof root.global === 'object' ? root.global : {};
  const audio = root.audio && typeof root.audio === 'object' ? root.audio : {};
  const rootDuration = normalizeDurationSummary(root, audio);
  const audioDuration = normalizeDurationSummary(audio, root);

  return {
    ...root,
    ...rootDuration,
    global: {
      ...global,
      total: toFiniteNumber(global.total) ?? 0,
      ...normalizeDurationSummary(global, root),
    },
    audio: {
      ...audio,
      uploaded: toFiniteNumber(audio.uploaded) ?? 0,
      accepted: toFiniteNumber(audio.accepted) ?? 0,
      rejected: toFiniteNumber(audio.rejected) ?? 0,
      ...audioDuration,
    },
    transcription: root.transcription && typeof root.transcription === 'object'
      ? {
          ...root.transcription,
          submitted: toFiniteNumber(root.transcription.submitted) ?? 0,
          validated: toFiniteNumber(root.transcription.validated) ?? 0,
          rejected: toFiniteNumber(root.transcription.rejected) ?? 0,
        }
      : root.transcription,
  };
};

export type ContributionHistoryStatus = 'validated' | 'rejected' | 'pending';

export interface ContributionHistoryItem {
  id: string;
  date: string | null;
  language: string | null;
  contentType: string;
  durationMs: number | null;
  status: ContributionHistoryStatus;
  /** Storage key to play an audio back; null for texts and theme answers. */
  blobName?: string | null;
  /**
   * The number the record holds in the document it was imported from, the
   * first row being 1. Null when it could not be established.
   */
  fileOrder?: number | null;
  /** The numeric identifier of that document. Null when it is unknown. */
  sourceFileId?: number | null;
}

export interface ContributionHistoryResponse {
  data: ContributionHistoryItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface ContributionHistoryFilters {
  page?: number;
  limit?: number;
  /** ISO date */
  dateDebut?: string;
  /** validated | rejected | pending, applied by the server before paging. */
  statut?: ContributionHistoryStatus;
  /** ISO date */
  dateFin?: string;
  langue?: string;
}

export const contributionsApi = {
  async getUserStats(
    userId: string,
    options?: { fresh?: boolean },
  ): Promise<ContributionsStatsResponse> {
    const { data } = await apiClient.get(`/users/user/${userId}/stats`, {
      params: options?.fresh ? { fresh: true } : undefined,
    });
    return normalizeUserStats(data);
  },

  /**
   * Historique de contribution consolidé (audio + texte + thématique),
   * paginé, filtrable par date et par langue.
   */
  async getUserHistory(
    userId: string,
    filters: ContributionHistoryFilters = {},
  ): Promise<ContributionHistoryResponse> {
    const params: Record<string, string | number> = {};
    if (filters.page != null) params.page = filters.page;
    if (filters.limit != null) params.limit = filters.limit;
    if (filters.dateDebut) params.date_debut = filters.dateDebut;
    if (filters.dateFin) params.date_fin = filters.dateFin;
    if (filters.langue) params.langue = filters.langue;
    if (filters.statut) params.statut = filters.statut;
    const { data } = await apiClient.get(
      `/users/user/${userId}/contributions`,
      { params },
    );
    return data;
  },
};